import React from "react";
import { Reveal, Stagger, StaggerItem } from "../../lib/motion";
import { Check, ArrowRight, Flame } from "lucide-react";
import { useApp } from "@/context/AppContext";
import useOrangeCareCatalog, { gbpMajor } from "@/hooks/useOrangeCareCatalog";

// Three core services: CRM (flagship), Website, Landing Page.
export default function Packages() {
  const { formatPrice, t } = useApp();
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  const crmF = t("services.crm_f") || [];
  const webF = t("services.web_f") || [];
  const lpF = t("services.lp_f") || [];

  // OrangeCare pricing lines come from the canonical backend catalog —
  // never from AppContext numeric values (which are now null placeholders).
  const { byKey: careByKey, status: careStatus } = useOrangeCareCatalog();
  const crmMonth = gbpMajor(careByKey.orangecare_crm?.prices?.GBP?.monthly);
  const webMonth = gbpMajor(careByKey.orangecare_website?.prices?.GBP?.monthly);
  const webYear  = gbpMajor(careByKey.orangecare_website?.prices?.GBP?.annual);
  const lpMonth  = gbpMajor(careByKey.orangecare_landing_page?.prices?.GBP?.monthly);
  const lpYear   = gbpMajor(careByKey.orangecare_landing_page?.prices?.GBP?.annual);
  const carePricingUnavailable = careStatus === "error";

  return (
    <section id="services" data-testid="services-section" className="relative py-20 md:py-28">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="max-w-2xl mb-10">
            <p className="text-[11px] tracking-[0.24em] uppercase text-black/75 mb-4 font-bold">
              {t("services.eyebrow")}
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-black">
              {t("services.title1")}<br />
              <span className="text-black/55">{t("services.title2")}</span>
            </h2>
          </div>
        </Reveal>

        <Stagger className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* 01 — CRM (flagship / wide) */}
          <StaggerItem className="lg:col-span-12" id="services-crm-wrap">
            <div
              id="crm-service"
              data-testid="service-card-crm"
              className="relative rounded-3xl p-8 md:p-12 bg-black text-white border border-black shadow-[0_50px_120px_-40px_rgba(0,0,0,0.6)] overflow-hidden"
            >
              <span
                data-testid="service-badge-crm"
                className="absolute top-6 right-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-[10px] font-bold tracking-[0.2em] uppercase shadow-lg"
              >
                <Flame size={11} strokeWidth={2.6} className="text-[#FF5A00]" />
                {t("services.badge_flagship")}
              </span>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-6">
                  <div className="text-[11px] text-white/60 tracking-[0.24em] uppercase font-bold">
                    {t("services.crm_index")} · {t("services.crm_sub")}
                  </div>
                  <h3 className="mt-3 font-display font-black text-4xl md:text-5xl text-white tracking-tight leading-[1.02]">
                    {t("services.crm_heading1")}<br />
                    <span className="text-white/60">{t("services.crm_heading2")}</span>
                  </h3>
                  <p className="mt-5 text-white/80 text-[15px] md:text-base leading-relaxed max-w-xl">
                    {t("services.crm_desc")}
                  </p>

                  <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4" style={{ fontVariantNumeric: "tabular-nums" }}>
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-white/60 font-bold">
                        {t("services.crm_price_label")}
                      </div>
                      <div className="mt-1 font-display font-black text-3xl md:text-4xl text-white tracking-tight" data-testid="service-crm-price">
                        {t("services.crm_tailored")}
                      </div>
                      <div className="mt-1 text-[12px] text-white/70" data-testid="service-crm-price-sub">
                        {t("services.crm_tailored_sub")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-white/60 font-bold">
                        {t("services.crm_care_label")}
                      </div>
                      {/* CRM OrangeCare is inherently bespoke — there is no
                          fixed public price for it. Displaying the same POA
                          treatment used by the Investment column keeps the
                          card intentional and avoids the empty pulsing
                          skeleton that previously flashed as a black
                          rectangle on the dark card (Phase 3I.4O.2 fix). */}
                      <div className="mt-1 font-display font-black text-3xl md:text-4xl text-white tracking-tight" data-testid="service-crm-care-price">
                        {t("services.crm_tailored")}
                      </div>
                      <div className="mt-1 text-[12px] text-white/70" data-testid="service-crm-care-price-sub">
                        {t("services.crm_tailored_sub")}
                      </div>
                    </div>
                  </div>

                  <p className="mt-6 text-white/70 text-[13px] leading-relaxed max-w-xl">
                    {t("services.crm_note")}
                  </p>

                  <button
                    data-testid="service-cta-crm"
                    onClick={() => go("#discovery")}
                    className="mt-8 group inline-flex items-center gap-3 h-14 px-8 rounded-full bg-white text-black font-display font-bold text-sm tracking-wide uppercase hover:bg-white/90 transition-all"
                  >
                    {t("services.crm_cta")}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform rtl:-scale-x-100" />
                  </button>
                </div>

                <div className="lg:col-span-6">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {crmF.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-[14px] text-white/95">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-white text-black grid place-items-center shrink-0">
                          <Check size={12} strokeWidth={3} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </StaggerItem>

          {/* 02 — Custom Website */}
          <StaggerItem className="lg:col-span-6">
            <div
              id="websites"
              data-testid="service-card-web"
              className="relative h-full rounded-3xl p-8 md:p-10 bg-white border border-black/15 flex flex-col"
            >
              <div className="text-[11px] tracking-[0.24em] uppercase text-black/60 font-bold">
                {t("services.web_index")} · {t("services.web_sub")}
              </div>
              <h3 className="mt-3 font-display font-black text-3xl md:text-4xl text-black tracking-tight">
                {t("services.web_heading")}
              </h3>
              <p className="mt-4 text-black/75 text-[15px] leading-relaxed">
                {t("services.web_desc")}
              </p>

              <div className="mt-6 pb-2 flex items-baseline flex-wrap gap-x-3" style={{ fontVariantNumeric: "tabular-nums" }}>
                <span className="text-[11px] tracking-[0.22em] uppercase text-black/60 font-bold">{t("services.web_price_label")}</span>
                <span className="font-display font-black text-4xl md:text-5xl text-black tracking-tight" data-testid="service-web-price">{formatPrice(t("services.web_price"), { base: "GBP" })}</span>
              </div>
              <p className="pb-6 border-b border-black/10 text-[11px] leading-snug text-black/55" data-testid="service-web-price-disclaimer">
                {t("services.web_price_disclaimer")}
              </p>
              <div className="mt-4 text-[13px] text-black/80" data-testid="service-web-care-line">
                <span className="font-bold">{t("services.web_care_label")}: </span>
                {carePricingUnavailable
                  ? "Pricing temporarily unavailable"
                  : webMonth != null && webYear != null
                    ? <>{formatPrice(webMonth, { base: "GBP" })}{t("services.per_month")} {t("services.or")} {formatPrice(webYear, { base: "GBP" })}{t("services.per_year")}</>
                    : "…"}
              </div>

              <ul className="space-y-2.5 mt-6 flex-1">
                {webF.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[14px] text-black/90">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-black text-white grid place-items-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid="service-cta-web"
                onClick={() => go("#discovery")}
                className="mt-8 group inline-flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm uppercase tracking-wide bg-black text-white hover:bg-black/85 transition-all"
              >
                {t("services.web_cta")}
                <ArrowRight size={15} className="rtl:-scale-x-100" />
              </button>
            </div>
          </StaggerItem>

          {/* 03 — Landing Page (launch offer) */}
          <StaggerItem className="lg:col-span-6">
            <div
              id="landing"
              data-testid="service-card-lp"
              className="relative h-full rounded-3xl p-8 md:p-10 bg-white border border-black/15 flex flex-col"
            >
              <span
                data-testid="service-badge-lp"
                className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white text-[10px] font-bold tracking-[0.2em] uppercase shadow-lg"
              >
                <Flame size={11} strokeWidth={2.6} className="text-[#FF5A00]" />
                {t("services.badge_offer")}
              </span>
              <div className="text-[11px] tracking-[0.24em] uppercase text-black/60 font-bold">
                {t("services.lp_index")} · {t("services.lp_sub")}
              </div>
              <h3 className="mt-3 font-display font-black text-3xl md:text-4xl text-black tracking-tight">
                {t("services.lp_heading")}
              </h3>
              <p className="mt-4 text-black/75 text-[15px] leading-relaxed">
                {t("services.lp_desc")}
              </p>

              <div className="mt-6 pb-6 border-b border-black/10" style={{ fontVariantNumeric: "tabular-nums" }}>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span data-testid="service-lp-original" className="text-black/55 text-xl md:text-2xl font-display font-medium line-through decoration-black/40">
                    {formatPrice(t("services.lp_normal"))}
                  </span>
                  <span className="font-display font-black text-5xl md:text-6xl tracking-tight text-black">
                    {formatPrice(t("services.lp_offer"))}
                  </span>
                </div>
                <p className="mt-2 text-[13px] font-bold tracking-wide text-black">
                  {t("services.save_word")} {formatPrice(t("services.lp_save"))} · {t("services.lp_save_pct")}
                </p>
              </div>
              <div className="mt-4 text-[13px] text-black/80" data-testid="service-lp-care-line">
                <span className="font-bold">{t("services.lp_care_label")}: </span>
                {carePricingUnavailable
                  ? "Pricing temporarily unavailable"
                  : lpMonth != null && lpYear != null
                    ? <>{formatPrice(lpMonth, { base: "GBP" })}{t("services.per_month")} {t("services.or")} {formatPrice(lpYear, { base: "GBP" })}{t("services.per_year")} · <span className="text-black/60">{t("services.lp_care_desc")}</span></>
                    : "…"}
              </div>

              <ul className="space-y-2.5 mt-6 flex-1">
                {lpF.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[14px] text-black/90">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-black text-white grid place-items-center shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid="service-cta-lp"
                onClick={() => go("#discovery")}
                className="mt-8 group inline-flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm uppercase tracking-wide bg-black text-white hover:bg-black/85 transition-all"
              >
                {t("services.lp_cta")}
                <ArrowRight size={15} className="rtl:-scale-x-100" />
              </button>
            </div>
          </StaggerItem>
        </Stagger>

        <Reveal>
          <p data-testid="services-footnote" className="mt-10 text-center text-[13px] text-black/75">
            {t("services.footnote")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
